<template>
  <div
    v-if="item?.is_link || hasGridGutter"
    :class="getWrapperClasses(Boolean(item?.is_link))"
  >
    <NuxtLink
      v-if="item?.is_link"
      :to="item?.link"
      :target="isExternal(item?.link) ? '_blank' : undefined"
      :rel="isExternal(item?.link) ? 'noopener noreferrer' : undefined"
      class="block h-full no-underline decoration-none"
    >
      <component
        :is="components[web_content_type?.web_component]"
        :item="item"
        :web_content_type="web_content_type"
        v-bind="componentContext"
      />
    </NuxtLink>
    <component
      v-else
      :is="components[web_content_type?.web_component]"
      :item="item"
      :web_content_type="web_content_type"
      v-bind="componentContext"
    />
  </div>
  <component
    v-else
    :is="components[web_content_type?.web_component]"
    :item="item"
    :web_content_type="web_content_type"
    v-bind="componentContext"
    :class="getWrapperClasses(false)"
  />
</template>

<script setup>
import { components, isExternal } from '~/composables/useUtils'
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  programme: {
    type: Object,
    default: undefined,
  },
  autoHeight: {
    type: Boolean,
    default: false,
  },
  gridGutter: {
    type: [String, Number],
    default: undefined,
  },
})

// Content types are build-time reference data, so fetch and retain the small catalogue once.
const getCachedContentTypes = (key, nuxtApp) => {
  return nuxtApp.payload.data[key] ?? nuxtApp.static.data[key]
}

const { data: web_content_types } = await useAsyncData(
  'web-content-types',
  () => queryContent('web_content_type').find(),
  {
    dedupe: 'defer',
    deep: false,
    getCachedData: getCachedContentTypes,
  }
)

const web_content_type = computed(() =>
  web_content_types.value?.find(
    (contentType) => contentType.id === props.item.web_content_type_id
  )
)

const componentContext = computed(() => {
  if (web_content_type.value?.web_component !== 'Subjects' || !props.programme) {
    return {}
  }

  return {
    programme: props.programme,
  }
})

const hasGridGutter = computed(() => {
  return props.gridGutter !== undefined && props.gridGutter !== null && props.gridGutter !== ''
})

const getWrapperClasses = (isLink) => {
  const contentType = web_content_type.value
  const heightClass = props.autoHeight
    ? 'h-auto'
    : contentType?.height
      ? `h-${contentType.height}`
      : ''
  const paddingClass = hasGridGutter.value
    ? `p-${props.gridGutter}`
    : 'p-0'

  if (contentType?.web_component !== 'Card') {
    if (isLink) {
      return [
        `${contentType?.break_pt}:${contentType?.width ?? 'w-full'}`,
        'col-12',
        heightClass,
        paddingClass,
      ].filter(Boolean)
    }

    return [`${contentType?.width}`, heightClass, paddingClass].filter(Boolean)
  }

  const width = contentType.width ?? 'w-full'
  const widthClasses = contentType.break_pt
    ? ['col-12', `${contentType.break_pt}:${width}`]
    : [width]

  return [
    ...widthClasses,
    heightClass,
    paddingClass,
  ].filter(Boolean)
}
</script>

<style lang="scss" scoped>
.decoration-none:link,
.decoration-none:visited {
  color: inherit !important;
  text-decoration: none !important;
}
</style>
